import { useState, Dispatch, SetStateAction } from 'react';
import { calculateSubtotal } from '../utils/calculation.utils';

interface TableRow {
  id: string;
  date: string;
  pricingType?: string;
  service: string;
  time: string;
  price: string;
  discount: { value: number; type: 'percentage' | 'fixed' };
  subtotal: number;
  [key: string]: any;
}

interface UseTableRowHandlersProps {
  tableRowsByFreelancer: Record<string, TableRow[]>;
  setTableRowsByFreelancer: Dispatch<SetStateAction<Record<string, TableRow[]>>>;
  lastUsedPricingTypeByFreelancer: Record<string, string>;
  setLastUsedPricingTypeByFreelancer: Dispatch<SetStateAction<Record<string, string>>>;
  calendarSelectedDatesByFreelancer: Record<string, Date[]>;
  setCalendarSelectedDatesByFreelancer: Dispatch<SetStateAction<Record<string, Date[]>>>;
  watch: (field: string) => any;
  hourlyPrice: string;
  isRutOrRotDeduction: boolean;
  serviceDescription: string;
}

// Parse a row date in ANY stored format (MM/DD/YYYY, YYYY/MM/DD, YYYY-MM-DD) →
// a UTC Date. Using UTC (Date.UTC) makes parsing/incrementing timezone-independent
// so no day is lost to local-timezone shifts (which broke the +1 in some setups).
const parseRowDate = (dateStr?: string): Date | null => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const s = dateStr.trim();
  let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);   // YYYY-MM-DD / YYYY/MM/DD
  if (m) return new Date(Date.UTC(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])));
  m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);       // MM/DD/YYYY / MM-DD-YYYY
  if (m) return new Date(Date.UTC(parseInt(m[3]), parseInt(m[1]) - 1, parseInt(m[2])));
  return null;
};
// Format a UTC Date as MM/DD/YYYY (canonical internal format), using UTC parts.
const formatRowDate = (d: Date): string =>
  `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;
// Today as MM/DD/YYYY from the user's LOCAL date (for the first row).
const formatTodayLocal = (): string => {
  const n = new Date();
  return `${String(n.getMonth() + 1).padStart(2, '0')}/${String(n.getDate()).padStart(2, '0')}/${n.getFullYear()}`;
};

export const useTableRowHandlers = ({
  tableRowsByFreelancer,
  setTableRowsByFreelancer,
  lastUsedPricingTypeByFreelancer,
  setLastUsedPricingTypeByFreelancer,
  calendarSelectedDatesByFreelancer,
  setCalendarSelectedDatesByFreelancer,
  watch,
  hourlyPrice,
  isRutOrRotDeduction,
  serviceDescription
}: UseTableRowHandlersProps) => {
  
  const handleRowChange = (freelancerId: string, rowId: string, field: string, value: any) => {
    // Track pricing type changes to remember user's preference PER FREELANCER
    if (field === 'pricingType') {
      setLastUsedPricingTypeByFreelancer(prev => ({
        ...prev,
        [freelancerId]: value
      }));
    }

    setTableRowsByFreelancer(prevRowsByFreelancer => {
      const prevRows = prevRowsByFreelancer[freelancerId] || [];
      
      const updatedRows = prevRows.map(row => {
        if (row.id === rowId) {
          const updatedRow = { ...row, [field]: value };
          
          // When pricing type changes, reset time to default value
          if (field === 'pricingType') {
            // If changing to 'Tim' (Hour), set default to '08.00'
            // For other types (Week, etc.), clear the time
            if (value === 'Tim') {
              updatedRow.time = '08.00';
            } else {
              updatedRow.time = '';
            }
           
            // Recalculate subtotal after pricing type change with new time value
            const subtotal = calculateSubtotal(updatedRow.time, parseFloat(row.price) || 0, row.discount);
            updatedRow.subtotal = subtotal;
          }
          
          // Recalculate subtotal when time, price, or discount changes
          if (field === 'time' || field === 'price' || field === 'discount') {
            const time = field === 'time' ? value : row.time;
            const price = field === 'price' ? value : row.price;
            const discount = field === 'discount' ? value : row.discount;
            
            // Calculate subtotal
            const subtotal = calculateSubtotal(time, parseFloat(price) || 0, discount);
            updatedRow.subtotal = subtotal;
          }
          
          return updatedRow;
        }
        return row;
      });
      
      return {
        ...prevRowsByFreelancer,
        [freelancerId]: updatedRows
      };
    });
  };

  const handleRemoveRow = (freelancerId: string, rowId: string) => {
    setTableRowsByFreelancer(prevRowsByFreelancer => {
      const prevRows = prevRowsByFreelancer[freelancerId] || [];
      const rowToRemove = prevRows.find(row => row.id === rowId);
      const updatedRows = prevRows.filter(row => row.id !== rowId);
      
      // Also update calendar dates - remove the date if no other row uses it
      if (rowToRemove?.date) {
        setCalendarSelectedDatesByFreelancer(prev => {
          const currentDates = prev[freelancerId] || [];
          
          // Check if any remaining row still uses this date
          const dateStillInUse = updatedRows.some(row => row.date === rowToRemove.date);
          
          if (!dateStillInUse) {
            // Parse the date to remove from calendar
            try {
              const dateParts = rowToRemove.date.split('/');
              if (dateParts.length === 3) {
                const [month, day, year] = dateParts;
                const dateToRemove = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                
                // Filter out the date from calendar
                const updatedCalendarDates = currentDates.filter(d => 
                  !(d.getFullYear() === dateToRemove.getFullYear() &&
                    d.getMonth() === dateToRemove.getMonth() &&
                    d.getDate() === dateToRemove.getDate())
                );
                
                return {
                  ...prev,
                  [freelancerId]: updatedCalendarDates
                };
              }
            } catch (error) {
              console.error('Error removing date from calendar:', error);
            }
          }
          
          return prev;
        });
      }
      
      return {
        ...prevRowsByFreelancer,
        [freelancerId]: updatedRows
      };
    });
  };

  const handleAddRow = (freelancerId: string) => {
    // Get the last used pricing type for THIS specific freelancer (default to 'Tim' if not set)
    const freelancerPricingType = lastUsedPricingTypeByFreelancer[freelancerId] || 'Tim';
    
    const newRow = {
      id: `row-${freelancerId}-${Date.now()}`,
      date: '',
      pricingType: freelancerPricingType,
      service: '',
      time: '',
      price: '',
      discount: { value: 0, type: 'percentage' as const },
      subtotal: 0
    };
    setTableRowsByFreelancer(prevRowsByFreelancer => {
      const prevRows = prevRowsByFreelancer[freelancerId] || [];
      const updatedRows = [...prevRows, newRow];
    
      return {
        ...prevRowsByFreelancer,
        [freelancerId]: updatedRows
      };
    });
  };

  // Function to add a new working hours section with next date
  const handleAddWorkingHoursSection = (freelancerId: string, sourceRow: any) => {
    setTableRowsByFreelancer(prevRowsByFreelancer => {
      const prevRows = prevRowsByFreelancer[freelancerId] || [];

      // Check if sourceRow already has a date (from timesheet mapping)
      // If so, use it directly instead of calculating next date
      const hasTimesheetDate = sourceRow.date && sourceRow.date.trim() !== '';
      let dateToUse = '';
      let dateObjToAdd: Date | null = null;

      
      if (hasTimesheetDate) {
        // Use the date from timesheet as-is
        dateToUse = sourceRow.date;

        // Parse date for calendar update
        try {
          const dateParts = dateToUse.split('/');
          if (dateParts.length === 3) {
            const [month, day, year] = dateParts;
            dateObjToAdd = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          }
        } catch (error) {
          console.error('Error parsing timesheet date:', error);
        }
      } else {
        // Find the maximum/latest date among all existing rows (format-robust:
        // handles MM/DD/YYYY, YYYY/MM/DD and YYYY-MM-DD). Uses `prevRows` from the
        // functional state updater, which is always the freshest state (never stale).
        let maxDate: Date | null = null;
        prevRows.forEach(row => {
          const rowDate = parseRowDate(row.date);
          if (rowDate && (!maxDate || rowDate > maxDate)) {
            maxDate = rowDate;
          }
        });

        if (maxDate) {
          // Next date = latest existing row's date + 1 day (UTC — no TZ shift).
          dateObjToAdd = new Date(maxDate as Date);
          dateObjToAdd.setUTCDate(dateObjToAdd.getUTCDate() + 1);
          dateToUse = formatRowDate(dateObjToAdd);
        } else {
          // No existing dated rows → this is the first row → use today (local).
          dateToUse = formatTodayLocal();
          dateObjToAdd = new Date();
        }
      }
      
      // Create new row - preserve all fields from sourceRow (especially for timesheet mapping)
      const newRow: TableRow = {
        id: sourceRow.id || `row-${freelancerId}-${Date.now()}`,
        workingHourId: sourceRow.workingHourId, // Preserve timesheet ID
        timeSheetWorkingHourId: sourceRow.timeSheetWorkingHourId, // Preserve timesheet reference ID
        date: dateToUse,
        pricingType: sourceRow.pricingType || 'Tim',
        pricingTypeArticleNumber: sourceRow.pricingTypeArticleNumber || '92',
        service: sourceRow.service || '',
        customWorkingDescription: sourceRow.customWorkingDescription || '', // Copy custom service description
        time: (sourceRow.pricingType === 'Tim' || !sourceRow.pricingType) 
          ? (sourceRow.time && sourceRow.time !== '00:00' ? sourceRow.time : '08:00')
          : (sourceRow.time || '1'),
        price: sourceRow.price || hourlyPrice || '500',
        discount: sourceRow.discount || { value: 0, type: 'percentage' as const },
        subtotal: sourceRow.subtotal || 0
      };

      // Also update calendar selected dates to include the new date
      if (dateObjToAdd) {
        setCalendarSelectedDatesByFreelancer(prev => {
          const currentDates = prev[freelancerId] || [];
          const dateExists = currentDates.some(d => 
            d.getFullYear() === dateObjToAdd.getFullYear() &&
            d.getMonth() === dateObjToAdd.getMonth() &&
            d.getDate() === dateObjToAdd.getDate()
          );
          if (!dateExists) {
            return {
              ...prev,
              [freelancerId]: [...currentDates, dateObjToAdd]
            };
          }
          return prev;
        });
      }
      
      return {
        ...prevRowsByFreelancer,
        [freelancerId]: [...prevRows, newRow]
      };
    });
  };

  return {
    handleRowChange,
    handleRemoveRow,
    handleAddRow,
    handleAddWorkingHoursSection
  };
};
